import React, { createContext, Dispatch, useReducer } from 'react';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import {
  getContextFactory,
  getDefinedContextFactory,
} from '@ax2/common/contexts/ContextFactory';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import { sortByOrder } from '@ax2/common/helpers/sort';
import ScreenerQuestionSchema from '@ax2/common/model/schemas/ScreenerQuestionSchema';

interface NewAction {
  type: 'new';
  survey: SurveySchema;
}
interface TitleAction {
  type: 'title';
  title: string | null;
}
interface QuestionsAction {
  type: 'questions';
  questions: QuestionSchema[];
  deleted?: QuestionSchema[];
}
interface ScreenersAction {
  type: 'screeners';
  screeners: ScreenerQuestionSchema[];
}

type Action = NewAction | TitleAction | QuestionsAction | ScreenersAction;

function reducer(
  state: SurveySchema | null,
  action: Action
): SurveySchema | null {
  if (action.type === 'new') {
    //TMP: fix survey order:
    const survey = {
      ...action.survey,
      questions: sortByOrder(action.survey.questions),
      deleted: sortByOrder(action.survey.deleted),
      screeners: sortByOrder(action.survey.screeners),
    };
    return survey;
  } else if (state) {
    switch (action.type) {
      case 'title': {
        return {
          ...state,
          ...{
            title: action.title,
          },
        };
      }
      case 'questions': {
        return {
          ...state,
          ...{
            questions: action.questions,
            deleted: action.deleted ? action.deleted : state.deleted,
          },
        };
      }
      case 'screeners': {
        return {
          ...state,
          ...{
            screeners: action.screeners,
          },
        };
      }
      default: {
        throw new Error(
          `Unknown Action type was used in survey context reducer`
        );
      }
    }
  } else {
    throw new Error('Attempt to reduce empty Survey context');
  }
}

const SurveyContext = createContext<SurveySchema | null>(null);
const SurveyDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

export const useSurveyContext = getContextFactory(SurveyContext);
export const useSurveyDispatchContext = getDefinedContextFactory(
  'SurveyDispatchContext',
  SurveyDispatchContext
);

export const SurveyContextProvider: React.FC = props => {
  const [survey, dispatch] = useReducer(reducer, null);

  return (
    <SurveyContext.Provider value={survey}>
      <SurveyDispatchContext.Provider value={dispatch}>
        {props.children}
      </SurveyDispatchContext.Provider>
    </SurveyContext.Provider>
  );
};
